import React, { ErrorInfo, ReactNode } from 'react';
import { Button } from 'antd-mobile';
import { MdRefresh } from 'react-icons/md';
import styles from './styles.module.scss';
import i18n from 'utils/i18n';
import { getAppName, getVersion, showData } from 'utils/common';

type Props = {
  children?: ReactNode;
};

type State = {
  hasError: boolean;
  message: string;
};

//NOTE: Cho phép xem trước màn hình sự cố mà không cần chờ crash thật, bằng cách
//thêm ?preview-crash=1 vào URL. Chỉ bật ở môi trường dev để tránh lộ trên production.
const isCrashPreview = () => {
  if (process.env.REACT_APP_DEV_MODE !== 'true') return false;

  try {
    return new URLSearchParams(window.location.search).get('preview-crash') === '1';
  } catch {
    return false;
  }
};

class ErrorBoundary extends React.Component<Props, State> {
  state: State = {
    hasError: isCrashPreview(),
    message: '',
  };

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      message: error?.message || '',
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    //NOTE: Giữ log lại để còn debug được khi user báo lỗi
    console.error('App crashed:', error, errorInfo?.componentStack);
  }

  componentDidMount() {
    //NOTE: React chỉ bắt lỗi lúc render. Lỗi trong hàm async (vd: gọi API trong useEffect)
    //lọt ra ngoài dạng unhandled rejection nên phải tự lắng nghe.
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    window.addEventListener('error', this.handleGlobalError);
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    window.removeEventListener('error', this.handleGlobalError);
  }

  //NOTE: Chỉ những lỗi lập trình dạng này mới thực sự làm hỏng UI. Lỗi API hay lỗi mạng
  //đã có toast xử lý riêng, không được phép kéo cả app vào màn hình sự cố.
  isFatalError = (error: any) => {
    if (!error) return false;
    if (error.isAxiosError) return false;

    return error instanceof TypeError || error instanceof ReferenceError;
  };

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (!this.isFatalError(event.reason)) return;
    console.error('Unhandled rejection:', event.reason);
    this.setState({
      hasError: true,
      message: event.reason?.message || '',
    });
  };

  handleGlobalError = (event: ErrorEvent) => {
    //NOTE: Bỏ qua lỗi tải tài nguyên (ảnh, script) vì không làm chết app
    if (!this.isFatalError(event.error)) return;
    console.error('Global error:', event.error);
    this.setState({
      hasError: true,
      message: event.error?.message || '',
    });
  };

  handleReload = () => {
    //NOTE: Xoá cache của service worker để chắc chắn nạp lại bản mới nhất
    const clearCaches =
      'caches' in window
        ? caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
        : Promise.resolve();

    clearCaches
      .catch(() => undefined)
      .finally(() => {
        //NOTE: Ở chế độ xem trước phải gỡ query param, nếu không reload sẽ quay lại
        //đúng màn hình sự cố và người dùng bị kẹt trong vòng lặp.
        if (isCrashPreview()) {
          const url = new URL(window.location.href);
          url.searchParams.delete('preview-crash');
          window.location.replace(url.toString());

          return;
        }

        window.location.reload();
      });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <p className={`font-xl color-white font-bold ${styles.title}`}>
            {i18n.t('title.error-crash')}
          </p>
          <p className={`font-md color-white ${styles.description}`}>
            {i18n.t('paragraph.error-crash')}
          </p>
          <Button color="warning" className={styles.action} onClick={this.handleReload}>
            <span className={styles.actionLabel}>
              <MdRefresh fontSize="22px" />
              {i18n.t('button.force-reload')}
            </span>
          </Button>
          <p className={`font-xs color-white ${styles.meta}`}>
            {getAppName()} - {showData(getVersion())}
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
